import { UrlUtils } from '@vectord/utils';

import apiRequest from 'utils/apiRequest';
import S3Error from './s3Error';

const defaultConfig = {
  bucket: process.env.REACT_APP_S3_ASSET_UPLOAD_BUCKET,
  region: process.env.REACT_APP_S3_ASSET_UPLOAD_REGION,
};

class UploadToS3 {
  constructor(config = defaultConfig) {
    this.bucket = config.bucket;
    this.region = config.region;
  }

  async updateAttachment(
    assetUploadUrl,
    attachment,
    attachmentId,
    extraParams = {}
  ) {
    const _attachment = new File(
      [attachment],
      this._s3FriendlyKey(attachment.name),
      { type: attachment.type }
    );

    const ownerId = attachmentId;
    const signedS3Post = await this._getAttachmentPostPolicy(
      ownerId,
      _attachment,
      extraParams
    );
    const formData = this._populateAttachmentFormData(
      signedS3Post,
      _attachment
    );
    const uploadStatus = await this._uploadToS3(formData);
    if (uploadStatus) {
      await this._updateAttachmentData(
        assetUploadUrl,
        {
          attachmentId: ownerId,
          fileName: _attachment.name,
          fileSize: _attachment.size,
          fileType: _attachment.type,
          lastModified: new Date(_attachment.lastModified).toString(),
        },
        extraParams
      );

      return { success: true, ownerId };
    } else {
      throw new S3Error(S3Error.S3_UPLOAD_FAILED);
    }
  }

  // Send a file in attachment
  async uploadAttachment(assetUploadUrl, attachment, extraParams = {}) {
    const _attachment = new File(
      [attachment],
      this._s3FriendlyKey(attachment.name),
      { type: attachment.type }
    );

    const uploadParams = {
      file_name: _attachment.name,
      file_type: _attachment.type,
    };

    const ownerId = await this._initializeAttachment(assetUploadUrl, {
      ...uploadParams,
      ...extraParams,
    });
    const signedS3Post = await this._getAttachmentPostPolicy(
      ownerId,
      _attachment,
      extraParams
    );

    const formData = this._populateAttachmentFormData(
      signedS3Post,
      _attachment
    );
    const uploadStatus = await this._uploadToS3(formData);

    if (uploadStatus) {
      const { url } = await this._updateAttachmentData(
        assetUploadUrl,
        {
          assetId: ownerId,
          fileName: _attachment.name,
          fileSize: _attachment.size,
          fileType: _attachment.type,
          lastModified: new Date(_attachment.lastModified).toString(),
        },
        extraParams
      );

      return { success: true, ownerId, url };
    } else {
      throw new S3Error(S3Error.S3_UPLOAD_FAILED);
    }
  }

  _createBucketUrl() {
    return `https://${this.bucket}.s3.amazonaws.com/`;
  }

  async _initializeAttachment(initializationUrl, uploadParams) {
    try {
      const resp = await apiRequest('POST', initializationUrl, uploadParams);
      return resp.data.id;
    } catch (error) {
      throw this._error(error, S3Error.ATTACHMENT_INITIALIZATION_FAILED);
    }
  }

  async _getAttachmentPostPolicy(ownerId, attachment, extraParams = {}) {
    const ppdParams = {
      owner_id: ownerId,
      owner_attribute: 'asset',
      owner_name: 'RawAsset',
      bucket: this.bucket,
      region: this.region,
      filename: attachment.name,
      filetype: attachment.type,
      filesize: attachment.size,
    };

    const ppdUrl = '/v3/scm/asset-management/raw-assets/paperclip-ppd';
    try {
      const response = await apiRequest('POST', ppdUrl, {
        ...ppdParams,
        ...extraParams,
      });
      if (response.success) {
        return response.signed_s3_post;
      } else {
        throw new this._controllerError(response.error);
      }
    } catch (error) {
      throw this._error(error, S3Error.PAPERCLIP_POSTPOLICY_FAILED);
    }
  }

  _createUploadForm(signedS3Post) {
    const formData = new FormData();
    const { key, acl, access_key: accessKey, policy, signature } = signedS3Post;
    formData.append('key', key);
    formData.append('acl', acl);
    formData.append('AWSAccessKeyId', accessKey);
    formData.append('policy', policy);
    formData.append('signature', signature);
    return formData;
  }

  _populateAttachmentFormData(signedS3Post, attachment) {
    const uploadForm = this._createUploadForm(signedS3Post);
    uploadForm.append('Content-Type', attachment.type);
    uploadForm.append('file', attachment);
    return uploadForm;
  }

  async _uploadToS3(formData) {
    const bucketUrl = this._createBucketUrl();
    try {
      const resp = await fetch(bucketUrl, {
        method: 'POST',
        mode: 'cors',
        body: formData,
        cache: 'no-cache',
      });
      return resp.ok;
    } catch (error) {
      throw this._error(error, S3Error.S3_UPLOAD_FAILED);
    }
  }

  async _updateAttachmentData(assetUploadUrl, params, extraParams) {
    try {
      const assetMetaUpdateUrl = UrlUtils.join(assetUploadUrl, params.assetId);
      return await apiRequest('PUT', assetMetaUpdateUrl, {
        ...{
          attachment_id: params.assetId,
          filename: params.fileName,
          filesize: params.fileSize,
          filetype: params.fileType,
          last_modified: params.lastModified,
        },
        ...extraParams,
      });
    } catch (error) {
      throw this._error(error, S3Error.FILE_UPDATION_FAILED);
    }
  }

  // Is thrown when the error is from rails server side.
  // These errors should be handled on your rails route only.
  _controllerError(msg) {
    const err = new Error(msg);
    err.isFromController = true;
    return err;
  }

  _error(error, errorCode) {
    if (error.isFromServer) {
      // s3 related errors
      return new S3Error(errorCode);
    } else if (error.isFromController) {
      return new Error(error.message);
    }
    return new S3Error(S3Error.NETWORK_PROBLEM_DETECTED);
  }

  _s3FriendlyKey(text, delimiter = '_') {
    return text.replace(/[^a-zA-Z0-9.]/g, delimiter);
  }
}

export default UploadToS3;
